import {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  createIcon,
  Flex,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { audioRecorder, detectVolumeAtom, detectVolumeRawAtom } from "../store";
import { DEFAULT_DETECT_VOLUME } from "../constants";
import { useToastMessage } from "../hooks/useToastMessage";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { MdCrop169 } from "react-icons/md";
import { ErrorDialog } from "../components/molecules/ErrorDialog";
import { ReadyState } from "../types";
import { ThresholdSlider } from "../components/atoms/ThresholdSlider";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { useDefaultPath } from "../hooks/useDefaultPath";
import {
  decibelToVolume,
  mimosysVolumeToVolume,
  volumeToDecibel,
  volumeToMimosysVolume,
} from "../utils/calcVolume";

const VoiceRecordIcon = createIcon({
  displayName: "VoiceRecordIcon",
  viewBox: "0 0 200 200",
  path: (
    <path
      fill="currentColor"
      d="M12.656,200.417a2.232,2.232,0,0,0,2.191-1.826L17.915,182a1.8,1.8,0,0,1,1.766-1.471c.05,0,15.485,1.241,15.485,1.241a11.381,11.381,0,0,0,2.251.242,10.19,10.19,0,0,0,10.138-9.787,1.689,1.689,0,0,1,.041-.329l3.077-13.553a2.234,2.234,0,0,0-1.008-2.4l-9.019-5.556a2.458,2.458,0,0,1,.722-4.483l9.166-2.011a2.228,2.228,0,0,0,1.752-2.3l-.473-9.072a1.8,1.8,0,0,1,1.8-1.894H65.1a6.263,6.263,0,0,0,5.657-8.923L60.03,92.889a1.859,1.859,0,0,1-.113-.658c.043-2.568.125-9.751.125-10.574a80.729,80.729,0,0,0-2.8-21.195C51.023,37.607,33.4,17.543,13.386,10.542a2.262,2.262,0,0,0-.737-.125,2.232,2.232,0,0,0-.737,4.339C30.6,21.3,47.08,40.134,52.929,61.636a76.4,76.4,0,0,1,2.645,20.021c0,.826-.1,10.176-.137,11.206a2.183,2.183,0,0,0,.134.852l11.006,29.6c.031.086.07.166.108.25a1.8,1.8,0,0,1-1.61,2.6l-15.711,0a2.236,2.236,0,0,0-2.232,2.354l.511,9.9a1.8,1.8,0,0,1-1.411,1.85l-5.779,1.272a6.921,6.921,0,0,0-2.15,12.655l6.564,4.044a1.8,1.8,0,0,1,.809,1.93l-2.522,11.138a2.291,2.291,0,0,0-.055.492,5.721,5.721,0,0,1-2.172,4.507A5.6,5.6,0,0,1,37.4,177.54a5.965,5.965,0,0,1-1.27-.142,2.342,2.342,0,0,0-.346-.055L16.54,175.79a2.272,2.272,0,0,0-2.357,1.819l-3.727,20.17a2.229,2.229,0,0,0,1.788,2.6A2.239,2.239,0,0,0,12.656,200.417Zm88.726-.406a2.23,2.23,0,0,0,1.582-.653,58.7,58.7,0,0,0,0-83.014,2.232,2.232,0,0,0-3.158,3.156,54.239,54.239,0,0,1,0,76.7,2.232,2.232,0,0,0,1.577,3.811ZM89.975,188.6a2.23,2.23,0,0,0,1.582-.653,42.622,42.622,0,0,0,0-60.2,2.232,2.232,0,1,0-3.158,3.156,38.147,38.147,0,0,1,0,53.883,2.233,2.233,0,0,0,1.577,3.814Zm100.143-4.32a10.311,10.311,0,0,0,10.3-10.3V85.25a10.31,10.31,0,0,0-10.3-10.3H145.752a10.311,10.311,0,0,0-10.3,10.3v29.967a2.233,2.233,0,1,0,4.466,0V85.25a5.84,5.84,0,0,1,5.834-5.832h20.165a1.8,1.8,0,0,1,1.8,1.8,2.234,2.234,0,0,0,4.469,0,1.8,1.8,0,0,1,1.8-1.8h16.133a5.84,5.84,0,0,1,5.834,5.832v88.734a5.842,5.842,0,0,1-5.834,5.834H145.752a5.842,5.842,0,0,1-5.834-5.834v-26.5a2.233,2.233,0,0,0-4.466,0v26.5a10.311,10.311,0,0,0,10.3,10.3Zm-111.55-7.09a2.212,2.212,0,0,0,1.577-.65,26.459,26.459,0,0,0,0-37.383,2.233,2.233,0,0,0-3.161,3.156,21.989,21.989,0,0,1,0,31.066,2.24,2.24,0,0,0,0,3.158A2.216,2.216,0,0,0,78.568,177.194Zm93.4-9.043a2.233,2.233,0,1,0,0-4.466H163.9a2.233,2.233,0,0,0,0,4.466Zm10.085-16.133a2.233,2.233,0,1,0,0-4.466H153.818a2.233,2.233,0,0,0,0,4.466Zm0-16.135a2.233,2.233,0,1,0,0-4.466H153.818a2.233,2.233,0,0,0,0,4.466Zm-44.367-2.3a2.233,2.233,0,1,0-2.234-2.232A2.235,2.235,0,0,0,137.685,133.584Zm44.367-13.834a2.417,2.417,0,0,0,1.622-.677,2.389,2.389,0,0,0,.612-1.555V97.35a2.374,2.374,0,0,0-.677-1.627,2.4,2.4,0,0,0-1.558-.607H153.818a2.193,2.193,0,0,0-2.232,2.234v20.167a2.2,2.2,0,0,0,2.232,2.232Zm-4.034-20.167a1.8,1.8,0,0,1,1.8,1.8v12.1a1.8,1.8,0,0,1-1.8,1.8H157.853a1.8,1.8,0,0,1-1.8-1.8v-12.1a1.8,1.8,0,0,1,1.8-1.8Z"
    />
  ),
});

function Description(props: Readonly<{ isStandby: boolean }>): ReactElement {
  const t = useModifiedTranslation();

  // テキスト中に含まれる<slider-icon>をIcon化する処理
  const SliderIcon = (): ReactElement => (
    <Icon as={MdCrop169} color="primary.theme_lv1" />
  );
  const textToSliderIcon = (text: string): ReactElement[] => {
    const parts = text.split("<slider-icon>");
    const components = parts.flatMap((part, index) => {
      return index < parts.length - 1
        ? [
            <Fragment key={index}>
              {part}
              <span
                style={{
                  display: "inline-block",
                  transform: "rotate(90deg) scaleX(1.5)",
                }}
              >
                <SliderIcon />
              </span>
            </Fragment>,
          ]
        : [<Fragment key={index}>{part}</Fragment>];
    });
    return components;
  };
  const description = t("Calibration.description");
  const descriptionComponents = textToSliderIcon(description);

  return (
    <VStack
      bgColor="common.calibration_description_bg"
      rounded="xl"
      padding={4}
      mt="auto"
    >
      <VoiceRecordIcon
        boxSize={{ base: 44, md: 56 }}
        color="primary.theme_lv1"
      />
      <Flex
        flexDirection="column"
        justifyContent="center"
        backgroundColor="primary.theme_lv3"
        rounded="xl"
        padding={2}
        h={{ base: 32 }}
      >
        <Box color="common_text.dark">
          <Text
            lineHeight="short"
            whiteSpace="pre-wrap"
            mx={{ base: 1, md: 5 }}
            fontSize="sm"
          >
            {props.isStandby ? t("Calibration.standby") : descriptionComponents}
          </Text>
        </Box>
      </Flex>
    </VStack>
  );
}

type StopRecordingFunc = () => void;
export function Calibration(
  props: Readonly<{
    backButton: boolean;
    initialSetup?: boolean;
  }>,
): ReactElement {
  const { i18n } = useTranslation();
  const t = useModifiedTranslation();
  const toastMessage = useToastMessage();
  const navigate = useNavigate();
  const defaultPath = useDefaultPath();
  const [detectVolumeMimosys] = useAtom(detectVolumeAtom);
  const [detectVolume] = useState(mimosysVolumeToVolume(detectVolumeMimosys));
  const [detectVolumeRaw, setDetectVolume] = useAtom(detectVolumeRawAtom);
  const [readyState, setReadyState] = useState<ReadyState>("UNINITIALIZED");
  const [currentAveragePower, setCurrentAveragePower] = useState(0);
  const [currentMaxPower, setCurrentMaxPower] = useState(0);
  const [logDetectVolume, setLogDetectVolume] = useState(
    volumeToDecibel(detectVolume),
  );
  const [stopRecording, setStopRecording] = useState<null | StopRecordingFunc>(
    null,
  );
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const resetSliderValue = useCallback(() => {
    const defaultLogDetectVolumeForMimosys = mimosysVolumeToVolume(
      DEFAULT_DETECT_VOLUME,
    );
    const defaultLogDetectVolume = volumeToDecibel(
      defaultLogDetectVolumeForMimosys,
    );
    setLogDetectVolume(defaultLogDetectVolume);
  }, []);

  const startRecording = useCallback(async () => {
    const updatingCallBack = (averagePower: number, maxPower: number): void => {
      setCurrentAveragePower(averagePower);
      setCurrentMaxPower(maxPower);
    };
    try {
      await audioRecorder.startRecording(
        updatingCallBack,
        true,
        undefined,
        undefined,
        true,
      );
    } catch (e) {
      console.warn(e);
      setErrorDialogMessage(
        e instanceof Error && e.name === "NotAllowedError"
          ? "Error.notAllowToUseMicrophone"
          : "Error.toUseMicrophone",
      );
      return;
    }
    const stop: StopRecordingFunc = () => {
      audioRecorder.stopRecording({});
      setStopRecording(null);
    };
    setStopRecording(() => stop);
  }, []);

  // detectVolumeRawがfalseの時は強制表示の為、遷移先はホーム画面
  const navigatePath = !detectVolumeRaw
    ? defaultPath
    : `/${i18n.resolvedLanguage}/` + "setting";

  const navigateAfterErrorDialog = (): void => {
    if (props.initialSetup) {
      // マイク権限がない場合はサービスを受けられないためログイン画面へ戻す
      navigate(`/${i18n.resolvedLanguage}/login`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  // 初期化処理
  useEffect(() => {
    if (readyState === "UNINITIALIZED") {
      setReadyState("INITIALIZING");
      audioRecorder
        .init()
        .then(() => setReadyState("READY"))
        .catch((e) => {
          const messageKey =
            e.name === "NotAllowedError"
              ? "Error.notAllowToUseMicrophone"
              : "Error.toUseMicrophone";
          setErrorDialogMessage(messageKey);
        });
    }
  }, [readyState, t, toastMessage]);

  // クリーンアップ処理
  useEffect(() => {
    return () => {
      setReadyState("FINISHED");
      audioRecorder.destroy().then();
    };
  }, []);

  const [showBackButton, setShowBackButton] = useState(false);
  useEffect(() => {
    if (stopRecording === null) {
      setShowBackButton(true);
    } else {
      setShowBackButton(false);
    }
  }, [stopRecording]);

  return (
    <>
      {/* マイクNGダイアログ */}
      <ErrorDialog
        messageKey={errorDialogMessage}
        onNavigate={navigateAfterErrorDialog}
      />
      <BasicPageLayout
        title={t("Calibration.title")}
        titleProps={{ fontSize: "2xl" }}
        showBackButton={showBackButton}
        minH="full"
        gap={2}
      >
        <Flex justify="flex-end">
          <Button
            size="md"
            onClick={resetSliderValue}
            color="common_text.light"
            bgColor="primary.theme_lv1"
            _hover={{
              background: "primary.theme_lv2",
              opacity: "0.8",
            }}
            _active={{
              background: "primary.theme_lv1",
              opacity: 0.5,
            }}
            visibility={stopRecording !== null ? "visible" : "hidden"}
          >
            {t("Calibration.reset")}
          </Button>
        </Flex>

        <Description isStandby={stopRecording === null} />

        <VStack
          borderColor="primary.theme_lv1"
          borderWidth={2}
          rounded="xl"
          px={5}
          py={2}
          justifyContent="center"
          visibility={stopRecording !== null ? "visible" : "hidden"}
        >
          <Box width="100%" marginBottom="10px" textAlign="left" fontSize="sm">
            {t("Calibration.volumegauge")}
          </Box>
          <ThresholdSlider
            averagePower={currentAveragePower}
            maxPower={currentMaxPower}
            logDetectVolume={logDetectVolume}
            onChangeLogDetectVolume={(val) => setLogDetectVolume(val)}
          />
        </VStack>

        {stopRecording !== null ? (
          <Flex columnGap={2} mt="auto">
            <Button
              variant="btn_primary"
              width="full"
              size="lg"
              onClick={() => {
                stopRecording();
                setLogDetectVolume(volumeToDecibel(detectVolume));
                navigate(navigatePath);
              }}
            >
              {t("Calibration.cancel")}
            </Button>
            <Button
              variant="btn_primary"
              width="full"
              size="lg"
              onClick={() => {
                stopRecording();
                const rawDetectVolume = decibelToVolume(logDetectVolume);
                const rawDetectVolumeForMimosys =
                  volumeToMimosysVolume(rawDetectVolume);
                setDetectVolume(rawDetectVolumeForMimosys);
                navigate(navigatePath);
              }}
            >
              {t("Calibration.set")}
            </Button>
          </Flex>
        ) : (
          <Button
            variant="btn_primary"
            width="full"
            size="lg"
            mt="auto"
            isLoading={readyState !== "READY"}
            onClick={() => startRecording()}
          >
            {t("Calibration.start")}
          </Button>
        )}
      </BasicPageLayout>
    </>
  );
}
